import { c as _c } from "react/compiler-runtime";
import { InfoBar } from "@components/common/info-bar/info-bar";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { teamDisplayProperties } from "@components/table/teams/teams-table-utils";
import { useTeamsAccess } from "@hooks/access-control/use-teams-access";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box } from "@mui-wrapper";
import { EditableTeamName } from "@pages/members/teams/team-details/editable-team-name";
import { getTeamDetailsInfoBarAction } from "@pages/members/teams/team-details/team-details-info-bar-action";
import { useAppParams } from "@router/router-helper";
import { useAppSelector } from "@store/store-helper";
import { selectedTeamAsMemberSelector, selectedTeamWithRoleSelector } from "@store/teams/teams-selector";
import { WorkspaceRoleDropdown } from "@components/role/workspace-role-dropdown";
import { TeamIcon } from "@flat-ui";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { capitalizeFirstLetter } from "@utils/string-utils";

/** Room kept next to the name for the pencil, and the save and cancel buttons while editing */
const EDIT_BUTTON_SPACE = "3.5rem";
/**
 * Info bar for the team overview tabs
 */
export function TeamDetailsInfoBar(t0) {
  const $ = _c(41);
  const {
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const selectedTeam = useAppSelector(selectedTeamWithRoleSelector);
  const selectedTeamAsMember = useAppSelector(selectedTeamAsMemberSelector);
  const {
    canEditTeams,
    canChangeTeamRole
  } = useTeamsAccess();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    teamTabs
  } = useAppParams();
  if (!selectedTeam || !selectedTeamAsMember || isLoading) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <InfoBar isLoading={true} />;
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    return t2;
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = capitalizeFirstLetter(TEAM_DISPLAY_NAME);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      display: "flex",
      alignItems: "center",
      gap: 1
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TeamIcon sx={{
      width: "20px",
      height: "20px"
    }} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = teamDisplayProperties[selectedTeam.category.kind];
  let t6;
  if ($[4] !== t5.color) {
    t6 = <SphereAvatar icon={t4} size="small" backgroundColor={t5.color} shouldHideWhiteRim={true} />;
    $[4] = t5.color;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      gridArea: "1 / 1",
      width: 0,
      minWidth: "100%"
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = canEditTeams ? EDIT_BUTTON_SPACE : 0;
  let t9;
  if ($[7] !== t8) {
    t9 = {
      gridArea: "1 / 1",
      minWidth: 0,
      content: "attr(data-value)",
      visibility: "hidden",
      whiteSpace: "pre",
      overflow: "hidden",
      fontSize: "1.125rem",
      fontWeight: "bold",
      pr: t8
    };
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      position: "absolute",
      top: "100%"
    };
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t9) {
    t11 = {
      display: "inline-grid",
      minWidth: 0,
      "& > *": t7,
      "&::after": t9,
      "& .MuiFormHelperText-root": t10
    };
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== canEditTeams || $[13] !== selectedTeam) {
    t12 = <EditableTeamName team={selectedTeam} isEditable={canEditTeams} />;
    $[12] = canEditTeams;
    $[13] = selectedTeam;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== selectedTeam.name || $[16] !== t11 || $[17] !== t12) {
    t13 = <Box data-value={selectedTeam.name} sx={t11}>{t12}</Box>;
    $[15] = selectedTeam.name;
    $[16] = t11;
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t13 || $[20] !== t6) {
    t14 = {
      label: t2,
      content: <Box sx={t3}>{t6}{t13}</Box>
    };
    $[19] = t13;
    $[20] = t6;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const t15 = !canChangeTeamRole;
  let t16;
  if ($[22] !== selectedTeamAsMember || $[23] !== t15) {
    t16 = {
      content: <WorkspaceRoleDropdown member={selectedTeamAsMember} isReadOnly={t15} />
    };
    $[22] = selectedTeamAsMember;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t14 || $[26] !== t16) {
    t17 = [t14, t16];
    $[25] = t14;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== selectedTeam || $[29] !== teamTabs) {
    t18 = getTeamDetailsInfoBarAction({
      selectedTeam,
      teamTabs
    });
    $[28] = selectedTeam;
    $[29] = teamTabs;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== isScreenSmOrSmaller || $[32] !== selectedTeam || $[33] !== teamTabs) {
    t19 = isScreenSmOrSmaller || !getTeamDetailsInfoBarAction({
      selectedTeam,
      teamTabs
    });
    $[31] = isScreenSmOrSmaller;
    $[32] = selectedTeam;
    $[33] = teamTabs;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = {
      content: t18,
      isHidden: t19
    };
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== t17 || $[39] !== t20) {
    t21 = <InfoBar items={t17} action={t20} />;
    $[38] = t17;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  return t21;
}